import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetChatsByOrder, useGetOrdersWithChats } from '~/api/chat';
import { formatOrderDisplayId, sellerLabel } from '~/utils';

import type {
  ChatType,
  GetBuyerOrdersWithChatsQuery,
  GetChatsByOrderQuery,
  TradePerspective,
} from '~/graphql/graphql';
import { OutboundEmailStatus } from '~/graphql/graphql';
import { PerspectiveCaption } from '~/components/ui/PerspectiveCaption';
import ConversationList from './sections/ConversationList';
import MessageThread from './sections/MessageThread';

type ChatItem = NonNullable<GetChatsByOrderQuery['chats']>[number];
type ChatOrder = NonNullable<NonNullable<ChatItem['orderReference']>['order']>;

export interface Conversation {
  id: string;
  name: string;
  initials: string;
  avatarBg: string;
  organizationName: string;
}

export interface TextMessage {
  id: string;
  type: 'text';
  content: string;
  timestamp: string;
  fromSelf: boolean;
}

export interface OrderNotificationMessage {
  id: string;
  type: 'order_notification';
  timestamp: string;
  order: ChatOrder | null;
  orderNumber: string;
  recipient: string;
  address: string;
  emailHtmlBody: string | null;
}

export interface OrderCancelledMessage {
  id: string;
  type: 'order_cancelled';
  timestamp: string;
  orderNumber: string;
  reason: string | null;
}

export interface OrderConfirmedMessage {
  id: string;
  type: 'order_confirmed';
  timestamp: string;
  orderNumber: string;
  emailHtmlBody: string | null;
}

export interface PriceDeclinedMessage {
  id: string;
  type: 'price_declined';
  timestamp: string;
  orderNumber: string;
  reason: string;
  emailHtmlBody: string | null;
  sellerView: boolean;
}

export interface OrderReminderMessage {
  id: string;
  type: 'order_reminder';
  timestamp: string;
  order: ChatOrder | null;
  orderNumber: string;
  recipient: string;
  address: string;
  emailHtmlBody: string | null;
}

export interface PackingListNotificationMessage {
  id: string;
  type: 'packing_list_notification';
  timestamp: string;
  orderId: string;
  orderNumber: string;
  chatType: 'PACKING_LIST_CREATED_BUYER' | 'PACKING_LIST_UPDATED_BUYER';
  emailHtmlBody: string | null;
}

export interface NotificationSentMessage {
  id: string;
  type: 'notification_sent';
  timestamp: string;
  orderNumber: string;
  kind: 'logistics' | 'vendor' | 'purchase_order';
  recipient: string | null;
  status: OutboundEmailStatus;
  emailHtmlBody: string | null;
}

export type ConversationMessage =
  | TextMessage
  | OrderNotificationMessage
  | OrderCancelledMessage
  | OrderConfirmedMessage
  | PriceDeclinedMessage
  | OrderReminderMessage
  | PackingListNotificationMessage
  | NotificationSentMessage;

type OrderItem = NonNullable<
  GetBuyerOrdersWithChatsQuery['buyerOrdersWithChats']
>[number];

const AVATAR_COLORS = [
  'bg-prim',
  'bg-status-label-500',
  'bg-status-info',
  'bg-warning-500',
  'bg-error-500',
];

function formatThreadTimestamp(instant: string): string {
  return new Date(instant).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const NOTIFICATION_SENT_KINDS: Partial<
  Record<ChatType, NotificationSentMessage['kind']>
> = {
  ORDER_CONFIRMED_INTERNAL_BUYER: 'logistics',
  ORDER_CONFIRMED_INTERNAL_SELLER: 'logistics',
  ORDER_CONFIRMED_LOGISTICS_PROVIDER: 'vendor',
  MSR_PURCHASE_ORDER_SELLER: 'purchase_order',
};

function chatToMessage(chat: ChatItem, isSeller: boolean): ConversationMessage {
  const timestamp = formatThreadTimestamp(chat.createdAt);
  const { order } = chat;
  const historicalOrder = chat.orderReference?.order ?? null;
  if (
    chat.type === 'ORDER_PLACED_BUYER' ||
    chat.type === 'ORDER_PLACED_SELLER'
  ) {
    const loc = order.shippingLocation;
    const address = loc
      ? [loc.name, loc.facility.address.city, loc.facility.address.region]
          .filter(Boolean)
          .join(', ')
      : '';

    return {
      id: chat.id,
      type: 'order_notification',
      timestamp,
      order: historicalOrder,
      orderNumber: formatOrderDisplayId(order),
      recipient:
        chat.email?.recipientTo ??
        (chat.type === 'ORDER_PLACED_BUYER'
          ? chat.buyingOrganization.name
          : sellerLabel(chat.sellingOrganization)),
      address,
      emailHtmlBody: chat.email?.htmlBody ?? null,
    };
  }
  if (chat.type === 'ORDER_CANCELLED_BUYER') {
    return {
      id: chat.id,
      type: 'order_cancelled',
      timestamp,
      orderNumber: formatOrderDisplayId(chat.order),
      reason: chat.order.cancellationNote ?? null,
    };
  }

  if (chat.type === 'ORDER_CONFIRMED_BUYER') {
    return {
      id: chat.id,
      type: 'order_confirmed',
      timestamp,
      orderNumber: formatOrderDisplayId(chat.order),
      emailHtmlBody: chat.email?.htmlBody ?? null,
    };
  }

  if (chat.type === 'ORDER_PLACED_SELLER_REMINDER') {
    const loc = chat.order.shippingLocation;
    const address = loc
      ? [loc.name, loc.facility.address.city, loc.facility.address.region]
          .filter(Boolean)
          .join(', ')
      : '';
    return {
      id: chat.id,
      type: 'order_reminder',
      timestamp,
      order: historicalOrder,
      orderNumber: formatOrderDisplayId(order),
      recipient:
        chat.email?.recipientTo ?? sellerLabel(chat.sellingOrganization),
      address,
      emailHtmlBody: chat.email?.htmlBody ?? null,
    };
  }

  if (chat.type === 'PRICE_NEGOTIATION_DECLINED_BUYER') {
    return {
      id: chat.id,
      type: 'price_declined',
      timestamp,
      orderNumber: formatOrderDisplayId(chat.order),
      reason: chat.title ?? '',
      emailHtmlBody: isSeller ? null : (chat.email?.htmlBody ?? null),
      sellerView: isSeller,
    };
  }

  if (
    chat.type === 'PACKING_LIST_CREATED_BUYER' ||
    chat.type === 'PACKING_LIST_UPDATED_BUYER'
  ) {
    return {
      id: chat.id,
      type: 'packing_list_notification',
      timestamp,
      orderId: chat.order.id,
      orderNumber: formatOrderDisplayId(chat.order),
      chatType: chat.type,
      emailHtmlBody: chat.email?.htmlBody ?? null,
    };
  }

  const notificationKind = NOTIFICATION_SENT_KINDS[chat.type];
  if (notificationKind) {
    return {
      id: chat.id,
      type: 'notification_sent',
      timestamp,
      orderNumber: formatOrderDisplayId(chat.order),
      kind: notificationKind,
      recipient: chat.email?.recipientTo ?? null,
      status: chat.email?.status ?? OutboundEmailStatus.Pending,
      emailHtmlBody: chat.email?.htmlBody ?? null,
    };
  }

  return {
    id: chat.id,
    type: 'text',
    content: chat.title ?? '',
    timestamp,
    fromSelf: false,
  };
}

function buildConversations(
  orders: OrderItem[],
  isSeller: boolean
): Conversation[] {
  return orders.map((order, index) => {
    const orgLabel = isSeller
      ? order.buyingOrganization.name
      : sellerLabel(order.sellingOrganization);
    return {
      id: order.id,
      name: `Order #${formatOrderDisplayId(order)}`,
      initials: orgLabel.charAt(0).toUpperCase(),
      avatarBg: AVATAR_COLORS[index % AVATAR_COLORS.length],
      organizationName: orgLabel,
    };
  });
}

export default function Messages({
  perspective,
}: {
  perspective: TradePerspective;
}) {
  const { t } = useTranslation('messages');
  const isSeller = perspective === 'SELLING';
  const pageTitle = t('messages.inbox_header');

  const { data: ordersData, isLoading: ordersLoading } =
    useGetOrdersWithChats(perspective);
  const orders =
    (isSeller
      ? ordersData && 'sellerOrdersWithChats' in ordersData
        ? ordersData.sellerOrdersWithChats
        : undefined
      : ordersData && 'buyerOrdersWithChats' in ordersData
        ? ordersData.buyerOrdersWithChats
        : undefined) ?? [];
  const conversations = buildConversations(orders, isSeller);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId = selectedId ?? conversations[0]?.id ?? null;
  const selected = conversations.find((c) => c.id === activeId) ?? null;

  const { data: chatsData, isLoading: chatsLoading } =
    useGetChatsByOrder(activeId);
  const messages = (chatsData?.chats ?? []).map((chat) =>
    chatToMessage(chat, isSeller)
  );

  if (ordersLoading) {
    return (
      <div className="flex h-[calc(100vh-130px)] items-center justify-center text-neut-sec text-sm">
        {t('messages.loading')}
      </div>
    );
  }

  return (
    <>
      <title>{pageTitle}</title>
      <div className="flex h-[calc(100vh-130px)] bg-neut-bg-alt border border-neut-border rounded-lg overflow-hidden">
        <ConversationList
          conversations={conversations}
          selectedId={activeId}
          onSelect={setSelectedId}
          header={pageTitle}
          subheader={<PerspectiveCaption perspective={perspective} />}
        />
        {selected && (
          <MessageThread
            conversation={selected}
            messages={messages}
            isLoading={chatsLoading}
          />
        )}
      </div>
    </>
  );
}
